import './WaitingHostButton.styles.scss';

import { observer } from 'mobx-react';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import React, { FC, SyntheticEvent, useMemo, useState } from 'react';
import { useStompClient } from 'react-stomp-hooks';

import { getPokerRoom } from '../../../../constants';
import { Permission } from '../../../../model/enumeration/Permission.enum';
import { WaitingHostBot } from '../../../../model/WaitingHostBot';
import authStore from '../../../../store/AuthStore';
import botManagementStore from '../../../../store/BotManagementStore';
import notificationStore from '../../../../store/NotificationStore';
import permissionStore from '../../../../store/PermissionStore';
import playerStore from '../../../../store/PlayerStore';
import {
  WaitingHostButtonProps,
  WaitingHostSource,
} from './WaitingHostButton.types';

const WaitingHostButton: FC<WaitingHostButtonProps> = ({ table }) => {
  const [isWaitingHostVisible, setIsWaitingHostVisible] = useState(false);
  const client = useStompClient();

  const hasManageBotsPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANAGE_BOTS),
    [authStore.authenticatedUser],
  );

  const waitingHostRebuyBots = useMemo(
    () => playerStore.findWaitingHostRebuyBots(table),
    [table.players],
  );
  const waitingHostBotsLength =
    table.waitingHostBots.length + waitingHostRebuyBots.length;

  const onWaitingHostButtonClick = (e: SyntheticEvent): void => {
    e.stopPropagation();
    setIsWaitingHostVisible(true);
  };

  const header = <div className="text-center">WAITING HOST</div>;

  const onRemoveBotFromWaitingHost = (bot: WaitingHostBot): void => {
    confirmDialog({
      message: (
        <>
          Are you sure you want to remove the bot (<b>{bot.pid}</b>)<br /> from
          waiting host?
        </>
      ),
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        botManagementStore.removeBotFromWaitingHost(
          client,
          table.id,
          getPokerRoom(),
          bot.pid,
        );
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: (
            <>
              Request sent for the bot (<b>{bot.pid}</b>) to leave waiting host
            </>
          ),
        });
      },
    });
  };

  const botCard = (
    bot: WaitingHostBot,
    source: WaitingHostSource,
  ): React.ReactElement => (
    <Card
      className="mb-3 bots-wrapper"
      pt={{
        content: {
          className: 'row align-items-center w-100',
        },
      }}
      header={
        hasManageBotsPermission &&
        source === WaitingHostSource.BUYIN && (
          <Button
            className="remove-btn"
            icon="pi pi-times"
            rounded
            text
            severity="danger"
            aria-label="Close"
            onClick={() => onRemoveBotFromWaitingHost(bot)}
          />
        )
      }
      key={`${bot.pid}-${bot.nick}`}
    >
      <Avatar
        className={`col-2 justify-content-center bot-avatar pe-0`}
        shape="circle"
        size="xlarge"
        image="/assets/bot.svg"
      />
      <div className="col-5 ps-4">
        <div className="info-field">
          <b>Pid:</b>
          {` ${bot.pid}`}
        </div>
        <div className="info-field">
          <b>Nick:</b>
          {` ${bot.nick}`}
        </div>
      </div>
      <div className="info-column source col-3">{source}</div>
      <div className="info-column amount col-2">{bot.amount}</div>
    </Card>
  );

  return (
    <>
      <Button
        className="WaitingHostButton-Component"
        icon="pi pi-hourglass"
        tooltip="Waiting host"
        severity="warning"
        rounded
        text
        onClick={onWaitingHostButtonClick}
        badge={
          waitingHostBotsLength > 0
            ? waitingHostBotsLength.toString()
            : undefined
        }
        badgeClassName="p-badge-warning m-0"
      />
      <Dialog
        onClick={e => e.stopPropagation()}
        visible={isWaitingHostVisible}
        onHide={() => setIsWaitingHostVisible(false)}
        header={header}
        className="WaitListButtonDialog-Component"
        data-ignore-outside-click
        maskClassName="ignore-outside-click"
      >
        {waitingHostRebuyBots.map(bot => botCard(bot, WaitingHostSource.REBUY))}
        {table.waitingHostBots.map(bot =>
          botCard(bot, WaitingHostSource.BUYIN),
        )}
      </Dialog>
    </>
  );
};

export default observer(WaitingHostButton);
